import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { DeleteUserDocument } from '../../graphql/__generated__/types';
import { deleteUserRefusalOf } from './deleteUserRefusal';

interface Props {
    user: { id: string; name: string; email: string };
    onClose: () => void;
    onDeleted: () => void;
}

const DeleteUserDialog = ({ user, onClose, onDeleted }: Props) => {
    const [acknowledged, setAcknowledged] = useState(false);

    const [remove, { loading, error }] = useMutation(DeleteUserDocument);

    const submit = () => {
        remove({ variables: { userId: user.id } })
            .then((result) => {
                if (result.data) {
                    onDeleted();
                }
            })
            .catch(() => {});
    };

    return (
        <Modal open onClose={onClose} title={c('Delete user').t`Delete this user`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">
                        <b>{user.name}</b>
                        <span className="text-text-600"> · {user.email}</span>
                    </p>

                    <Alert type="warning">
                        {c('Delete user')
                            .t`Deleting removes this person and everything that hangs from them, for good: every account attachment, their perimeter, the dashboards granted to them and their partner list. It cannot be undone. Their open sessions are ended and any pending invitation is cancelled, so their next request is refused. The e-mail can be onboarded again afterwards, with fresh access.`}
                    </Alert>

                    <p className="text-xs text-text-600">
                        {c('Delete user')
                            .t`If this person is the last administrator of an account, deleting them leaves it with nobody to administer it. Check that first, since it is not prevented.`}
                    </p>

                    <label className="flex items-start gap-2 text-sm text-text-900">
                        <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={acknowledged}
                            onChange={(event) => setAcknowledged(event.target.checked)}
                        />
                        <span>
                            {c('Delete user').t`I understand this is permanent and removes all of their access.`}
                        </span>
                    </label>

                    {error && <RefusalNotice refusal={deleteUserRefusalOf(error)} />}
                </div>
            </ModalContent>

            <ModalFooter>
                <Button category="weak" onClick={onClose}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button category="destructive" loading={loading} disabled={!acknowledged} onClick={submit}>
                    {c('Delete user').t`Delete the user`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default DeleteUserDialog;
